import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, unref as _unref } from "vue"

const _hoisted_1 = { class: "project-orders" }
const _hoisted_2 = { class: "table__footer" }
const _hoisted_3 = {
  key: 1,
  class: "empty"
}
const _hoisted_4 = { class: "bottom-buttons" }

import { computed, ref, onMounted, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import dayjs from "dayjs";
import { GET_PROJECT_ORDERS } from "@/graphql/project/queries";
import { type PaginatedResult } from "@/helpers/typesDefinition";
import { type Project } from "@/helpers/project/typesDefinition";
import { type Order, RecurringOrder } from "@/helpers/order/typesDefinition";
import { formatPrice } from "@/helpers/utils";
import { formatPeriodDateShort } from "@/helpers/order/utils";
import checkHasAccess from "@/helpers/permissions/checkHasAccess";
import BasicButton from "@/components/BasicButton.vue";
import OrderDetails from "@/components/order/OrderDetails.vue";
import TableBase from "@/components/TableBase.vue";
import AddOrder from "@/components/order/AddOrder.vue";
import RecurringOrderDetails from "@/components/order/RecurringOrderDetails.vue";

type OrderProject = Project & {
  recurringOrders: RecurringOrder[];
  orders: PaginatedResult<
    Pick<
      Order,
      | "id"
      | "title"
      | "startDate"
      | "endDate"
      | "dueDate"
      | "status"
      | "price"
      | "paymentOption"
      | "invoiced"
      | "company"
    >
  >;
};

type GetProjectOrdersResult = {
  project: OrderProject;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectOrders',
  props: {
    id: {},
    orderId: {}
  },
  setup(__props: any) {

const props = __props;

const { t } = useI18n({
  inheritLocale: true,
  useScope: "global",
});

const toast = useToast();

const getProjectOrdersVariables = computed(() => {
  const projectId = props.id;
  if (projectId) {
    return {
      offset: 0,
      projectId,
    };
  }
  return {};
});

const { result, fetchMore } = useQuery<GetProjectOrdersResult>(
  GET_PROJECT_ORDERS,
  getProjectOrdersVariables,
);

const orders = computed(() => {
  return result.value?.project?.orders?.items;
});

const recurringOrders = computed(() => {
  return result.value?.project?.recurringOrders;
});

const project = computed(() => {
  return result.value?.project;
});

const hasMore = computed(() => {
  return result.value?.project.orders?.hasMore;
});

const loadMore = () => {
  if (hasMore.value) {
    fetchMore({
      variables: {
        projectId: props.id,
        offset: orders.value?.length,
      },
    });
  }
};

const modalDetailsId = ref(props.orderId ?? "");
const isModalDetailsVisible = computed(() => Boolean(modalDetailsId.value));
const selectOrder = (orderId: string) => {
  modalDetailsId.value = orderId;
};

function closeOrderDetailsModal(): void {
  modalDetailsId.value = "";
}

const hasAdminAccess = ref(false);
const hasClientAccess = ref(false);
const readOnly = ref(true);

onMounted(async () => {
  hasAdminAccess.value = await checkHasAccess(
    result.value?.project?.client?.id,
    props.id,
    "admin",
    toast,
    t("errors.getUserDetails"),
  );
});

watch([result], async ([r]) => {
  const clientId = r?.project?.client?.id;
  readOnly.value = !r?.project?.active;
  hasClientAccess.value = await checkHasAccess(
    clientId,
    props.id,
    "client",
    toast,
    t("errors.getUserDetails"),
  );
});

const columns = [
  {
    name: "period",
    title: t("labels.order.date"),
  },
  {
    name: "title",
    title: t("labels.order.title"),
  },
  {
    name: "paymentOptionLabel",
    title: t("labels.order.paymentOption"),
  },
  {
    name: "statusLabel",
    title: t("labels.order.status"),
  },
  {
    name: "priceLabel",
    title: t("labels.ordered"),
  },
  {
    name: "companyName",
    title: t("labels.order.company"),
  },
];

const recurringOrdersColumns = [
  {
    name: "period",
    title: t("labels.order.date"),
  },
  {
    name: "title",
    title: t("labels.order.title"),
  },
  {
    name: "statusLabel",
    title: t("labels.order.status"),
  },
  {
    name: "priceLabel",
    title: t("labels.ordered"),
  },
  {
    name: "companyName",
    title: t("labels.order.company"),
  },
];

const getStatusLabel = (status: Order["status"]) => {
  switch (status) {
    case "retainer_sent":
    case "retainer_reminder_1":
    case "retainer_reminder_2":
    case "retainer_paid":
    case "performed":
    case "postpaid_draft":
    case "postpaid_invoiced":
    case "postpaid_reminder_1":
    case "postpaid_reminder_2":
    case "postpaid_reminder_3":
    case "closed":
    case "late_payment":
      return "approved";
    default:
      return status;
  }
};

const formattedOrders = computed(() => {
  if (!orders.value?.length) return [];
  return orders.value?.map((order) => {
    const orderStatus = getStatusLabel(order.status);
    const statusLabel = t(`statusLabels.${orderStatus}`);
    return {
      ...order,
      period: formatPeriodDateShort(order),
      paymentOptionLabel: t(`paymentOptionLabels.${order.paymentOption}`),
      statusLabel:
        orderStatus == "late_approval"
          ? {
              className: "text-warning",
              value: statusLabel,
            }
          : statusLabel,
      orderStatus,
      priceLabel: formatPrice(order.price),
      companyName: order.company.name,
    };
  });
});

const sortedOrders = computed(() => {
  const orders = [...formattedOrders.value];
  return orders.sort((orderA, orderB) => {
    const dateA = dayjs(orderA.startDate);
    const dateB = dayjs(orderB.startDate);

    return dateB.diff(dateA);
  });
});

const formattedRecurringOrders = computed(() => {
  if (!recurringOrders.value?.length) return [];
  return recurringOrders.value?.map((order) => {
    const orderStatus = getStatusLabel(order.status);
    const statusLabel = t(`statusLabels.${orderStatus}`);
    return {
      ...order,
      period: formatPeriodDateShort(order),
      statusLabel:
        orderStatus == "late_approval"
          ? {
              className: "text-warning",
              value: statusLabel,
            }
          : statusLabel,
      orderStatus,
      priceLabel: formatPrice(order.price),
      companyName: order.company.name,
    };
  });
});

const sortedRecurringOrders = computed(() => {
  const orders = [...formattedRecurringOrders.value];
  return orders.sort((orderA, orderB) => {
    const dateA = dayjs(orderA.startDate);
    const dateB = dayjs(orderB.startDate);

    return dateB.diff(dateA);
  });
});

const isAddOrderModalVisible = ref(false);
function showAddOrderModal(): void {
  isAddOrderModalVisible.value = true;
}

function closeAddOrderModal(): void {
  isAddOrderModalVisible.value = false;
}

const refetchQueries = computed(() => [
  {
    query: GET_PROJECT_ORDERS,
    variables: getProjectOrdersVariables.value,
  },
]);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(TableBase, {
      columns: columns,
      items: sortedOrders.value,
      onSelectItem: selectOrder,
      "is-clickable": true
    }, {
      actions: _withCtx(({ item: order }) => [
        (
            !readOnly.value && order.orderStatus !== 'approved' && !hasAdminAccess.value
          )
          ? (_openBlock(), _createBlock(BasicButton, {
              key: 0,
              color: "neutral"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("buttons.order.approve")), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["items"]),
    _createElementVNode("div", _hoisted_2, [
      (hasMore.value)
        ? (_openBlock(), _createBlock(BasicButton, {
            key: 0,
            onClick: loadMore
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("buttons.order.loadMore")), 1)
            ]),
            _: 1
          }))
        : (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(_ctx.$t("orders.allLoaded")), 1))
    ]),
    (sortedRecurringOrders.value?.length)
      ? (_openBlock(), _createBlock(TableBase, {
          key: 0,
          columns: recurringOrdersColumns,
          items: sortedRecurringOrders.value,
          onSelectItem: selectOrder,
          "is-clickable": true
        }, {
          actions: _withCtx(({ item: order }) => [
            (
            !readOnly.value && order.orderStatus !== 'approved' && !hasAdminAccess.value
          )
              ? (_openBlock(), _createBlock(BasicButton, {
                  key: 0,
                  color: "neutral"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("buttons.order.approve")), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["items"]))
      : _createCommentVNode("", true),
    _cache[4] || (_cache[4] = _createElementVNode("div", { class: "table__footer" }, null, -1)),
    _createElementVNode("div", _hoisted_4, [
      (project.value && project.value.active && hasAdminAccess.value)
        ? (_openBlock(), _createBlock(BasicButton, {
            key: 0,
            onClick: _cache[0] || (_cache[0] = ($event: any) => (showAddOrderModal())),
            class: "add-button"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("titles.order.add")), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    (
        project.value &&
        project.value.active &&
        project.value.client &&
        hasAdminAccess.value &&
        isAddOrderModalVisible.value
      )
      ? (_openBlock(), _createBlock(AddOrder, {
          key: 1,
          onClose: _cache[1] || (_cache[1] = ($event: any) => (closeAddOrderModal())),
          project: project.value,
          refetchQueries: refetchQueries.value
        }, null, 8, ["project", "refetchQueries"]))
      : _createCommentVNode("", true),
    (isModalDetailsVisible.value)
      ? (_openBlock(), _createBlock(RecurringOrderDetails, {
          key: 2,
          onClose: _cache[2] || (_cache[2] = ($event: any) => (closeOrderDetailsModal())),
          id: modalDetailsId.value,
          projectId: __props.id,
          hasAdminAccess: hasAdminAccess.value,
          "read-only": readOnly.value
        }, null, 8, ["id", "projectId", "hasAdminAccess", "read-only"]))
      : _createCommentVNode("", true),
    (isModalDetailsVisible.value)
      ? (_openBlock(), _createBlock(OrderDetails, {
          key: 3,
          id: modalDetailsId.value,
          onClose: _cache[3] || (_cache[3] = ($event: any) => (closeOrderDetailsModal())),
          projectId: __props.id,
          "has-client-access": hasClientAccess.value,
          hasAdminAccess: hasAdminAccess.value,
          "read-only": readOnly.value
        }, null, 8, ["id", "projectId", "has-client-access", "hasAdminAccess", "read-only"]))
      : _createCommentVNode("", true)
  ]))
}
}

})